import { useCallback, useEffect, useState, useMemo } from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

// Components
import Select from '../../../../components/atoms/select';
import Button from '../../../../components/atoms/button';
import Table, { TableColumn } from '../../../../components/organisms/table';
import Text from '../../../../components/atoms/text';
import Pagination, {
	PaginationProps,
} from '../../../../components/organisms/pagination';
import { NotificationToast } from '../../../../components/organisms/notification-toast';

// Services
import YardService, { IYard } from '../../../../services/Yard.service';
import ShipService, { IShip } from '../../../../services/Ship.service';

// Utils & Types
import { EditableChangeEvent } from '../../../../utils/types/editable';
import { dateObjToStr } from '../../../../utils/date';
import { HomeLoaderData } from '../../../../types/homeLoaderData';
import ContainerService, {
	IContainer,
} from '../../../../services/Container.service';
import Input from '../../../../components/atoms/input';
import MonitoringService, {
	MonitoringR,
} from '../../../../services/Monitoring.service';
import InputCalendar from '../../../../components/molecules/input-calendar';

// ================ Constants & Types ================
interface MonitoringForm {
	yard: Partial<IYard>;
	dateRetroactive?: Date;
}

const DEFAULT_FORM: MonitoringForm = {
	yard: {},
	dateRetroactive: undefined,
};

const DEFAULT_PAGINATION: PaginationProps = {
	pageCurrent: 1,
	pageSize: 10,
	totalItems: 0,
	totalPages: 1,
};

// ================ Main Component ================
const AuditoryMonitoring = () => {
	const { t } = useTranslation();
	const { accountId } = useRouteLoaderData('home') as HomeLoaderData;

	// ================ State ================
	const [yards, setYards] = useState<IYard[]>([]);
	const [form, setForm] = useState<MonitoringForm>(DEFAULT_FORM);
	const [searchTrigger, setSearchTrigger] = useState<boolean>(false);
	const [monitorings, setMonitorings] = useState<MonitoringR[]>([]);
	const [pagination, setPagination] =
		useState<PaginationProps>(DEFAULT_PAGINATION);

	// ================ Table Columns ================
	const columns: TableColumn<MonitoringR>[] = useMemo(
		() => [
			{
				key: 'actions',
				label: t('misc.remove'),
				children: (row) => (
					<input
						type="checkbox"
						checked={row.checked}
						onClick={() => handleCheck(row)}
					/>
				),
			},
			{ key: 'container', label: t('port_management.container') },
			{
				key: 'date',
				label: t('date.title'),
				children: (row) => (
					<Text className="text-xs">
						{dateObjToStr(row.date, true)}
					</Text>
				),
			},
			{
				key: 'setPoint',
				label: t('port_management.containers.set_point'),
			},
			{
				key: 'temperature',
				label: t('port_management.containers.temperature'),
			},
			{
				key: 'travelCode',
				label: t('movement.travel_code'),
			},
		],
		[t, monitorings]
	);

	const fetchYards = useCallback(async () => {
		if (!accountId) return;

		try {
			const response = await YardService.getAll({
				isActive: 1,
				idClient: Number(accountId),
			});

			if (response?.data) {
				setYards(response.data);
			}
		} catch (error) {
			NotificationToast.Warn(t('errors.fetch_yards'));
		}
	}, [accountId, t]);

	const fetchMonitoring = useCallback(async () => {
		if (!form.yard.id) return;
		try {
			const response = await MonitoringService.getAllAuditory(
				{
					pageCurrent: pagination.pageCurrent,
					pageSize: pagination.pageSize,
					idClient: Number(accountId),
				},
				form.yard?.id,
				form.dateRetroactive
			);

			if (response) {
				setMonitorings(response.data);
				setPagination((prev) => ({
					...prev,
					totalItems: response.totalItems,
					totalPages: response.totalPages,
				}));
			}
		} catch (error) {
			NotificationToast.Warn(t('errors.fetch_movements'));
		}
	}, [pagination.pageCurrent, pagination.pageSize, searchTrigger]);

	// ================ Effects ================
	useEffect(() => {
		fetchYards();
	}, [fetchYards]);

	useEffect(() => {
		fetchMonitoring();
	}, [fetchMonitoring]);

	// ================ Handlers ================
	const handleCheck = (monitoring: MonitoringR) => {
		const index = monitorings.findIndex((i) => i.id == monitoring.id);
		const newData = [...monitorings];
		newData[index].checked = !newData[index].checked;
		setMonitorings(newData);
	};

	const handleYardChange = useCallback(
		(e: EditableChangeEvent) => {
			const yard = yards.find((y) => y.name === e.target.value);
			if (!yard) return;

			setForm((prev) => ({ ...prev, yard }));
		},
		[yards]
	);

	const handleSearch = useCallback(() => {
		setSearchTrigger((prev) => !prev);
	}, []);

	const handleResetFilters = useCallback(() => {
		// Reset form to default values
		setForm(DEFAULT_FORM);
		// Trigger search to show all results
		setSearchTrigger((prev) => !prev);
		// Reset to first page
		setPagination((prev) => ({ ...prev, pageCurrent: 1 }));
	}, []);

	const handlePaginationUpdate = useCallback(
		(pageSize: number, pageCurrent: number) => {
			setPagination((prev) => ({ ...prev, pageSize, pageCurrent }));
		},
		[]
	);

	const handleSave = async () => {
		const ids = [];
		for (let container of monitorings) {
			if (container.checked) ids.push(container.id);
		}
		if (ids.length == 0) {
			NotificationToast.Warn(
				t('port_management.monitoring.not_selected')
			);
			return;
		}
		const response = await ContainerService.deactivate(ids);
		if (response) {
			NotificationToast.Success(t('alert.success.generic'));
			handleSearch();
		}
	};

	const handleDateChange = (date: Date) => {
		setForm((prev) => ({ ...prev, dateRetroactive: date }));
	};

	// ================ Render Helpers ================
	const renderFilters = () => (
		<div className="flex gap-x-8 gap-y-4 flex-wrap items-end">
			<Select
				boxClassName="min-w-60"
				name="yard"
				value={form.yard.name || ''}
				onChange={handleYardChange}
				label={t('port_management.yard')}
				placeholder={t('port_management.yard')}
				options={yards.map((y) => y.name)}
				disabled={!accountId}
			/>

			<InputCalendar
				className="min-w-60"
				name="date"
				onDateChange={handleDateChange}
				label={t('date.retroactive')}
				placeholder={t('date.retroactive')}
			/>

			<div className="flex gap-2">
				<Button onClick={handleSearch}>{t('misc.search')}</Button>

				<Button theme="light" onClick={handleResetFilters}>
					{t('misc.reset')}
				</Button>
			</div>
		</div>
	);

	const renderTable = () => (
		<div className="overflow-x-auto w-full flex flex-col gap-8">
			<Table
				data={monitorings}
				columns={columns}
				className={classNames('grid-cols-[auto_1fr_1fr_1fr_1fr_1fr]')}
			/>
		</div>
	);

	// ================ Render ================
	return (
		<div className="flex flex-col gap-4 px-8 py-4">
			{renderFilters()}
			{renderTable()}
			<Button className="w-fit self-end" onClick={handleSave}>
				{t('misc.save')}
			</Button>
			<Pagination {...pagination} onUpdate={handlePaginationUpdate} />
		</div>
	);
};

export default AuditoryMonitoring;
